import { Device } from './Device';
import { DeviceInfo } from '../types/device';
import { deviceApiService, DeviceConnectionInfo } from './deviceApiService';
import { ApiClient } from '../utils/apiClient';
// TODO: 更新为MQTT实现

type DeviceManagerEvents = {
  deviceStatusChanged: () => void;
  deviceListUpdated: (devices: Device[]) => void;
  statisticsUpdated: (statistics: DeviceManagerStatistics) => void;
  connectionStateChanged: (state: string) => void;
  error: (error: Error) => void;
};

export interface DeviceManagerStatistics {
  total: number;
  online: number;
  audioEnabled: number;
  pttActive: number;
}

export class DeviceManager {
  private devices: Map<string, Device> = new Map();
  private static instance: DeviceManager;
  private eventListeners: Map<keyof DeviceManagerEvents, Set<Function>> = new Map();
  private isInitialized = false;
  
  private constructor() {
    // 初始化事件监听器Map
    this.eventListeners.set('deviceStatusChanged', new Set());
    this.eventListeners.set('deviceListUpdated', new Set());
    this.eventListeners.set('statisticsUpdated', new Set());
    this.eventListeners.set('connectionStateChanged', new Set());
    this.eventListeners.set('error', new Set());
  }
  
  public static getInstance(): DeviceManager {
    if (!DeviceManager.instance) {
      DeviceManager.instance = new DeviceManager();
    }
    return DeviceManager.instance;
  }
  
  /**
   * 初始化设备管理器
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    try {
      console.log('DeviceManager: 初始化设备管理器');
      
      // 设置超时机制，防止初始化时间过长
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('设备管理器初始化超时')), 12000); // 12秒超时
      });
      
      const initPromise = this.performInitialization();
      
      // 使用Promise.race来设置超时
      await Promise.race([initPromise, timeoutPromise]);
      
      console.log('DeviceManager: 设备管理器初始化完成');
      
    } catch (error) {
      console.error('DeviceManager: 初始化失败:', error);
      this.emit('error', error as Error);
      // 不抛出错误，允许服务继续运行
      console.warn('DeviceManager: 初始化失败，但服务将继续运行');
    }
  }

  /**
   * 执行实际的初始化逻辑
   */
  private async performInitialization(): Promise<void> {
    try {
      // 初始化设备状态服务
      console.log('DeviceManager: 开始初始化设备状态服务');
      // TODO: 重构为MQTT架构
      // await deviceStatusService.initialize();
      console.log('DeviceManager: 设备状态服务初始化完成');
      
      // 设置事件监听
      console.log('DeviceManager: 开始设置事件监听');
      this.setupEventListeners();
      console.log('DeviceManager: 事件监听设置完成');
      
      // 尝试获取初始设备列表，避免白屏
      try {
        console.log('DeviceManager: 开始获取初始设备列表');
        await this.fetchDevices();
        console.log('DeviceManager: 初始设备列表获取成功');
      } catch (fetchError) {
        console.warn('DeviceManager: 初始设备列表获取失败，但服务将继续运行:', fetchError);
        // 不抛出错误，允许服务继续运行，避免阻塞整个应用
      }
      
      this.isInitialized = true;
      
    } catch (error) {
      console.error('DeviceManager: 执行初始化失败:', error);
      throw error;
    }
  }
  
  /**
   * 设置事件监听
   */
  private setupEventListeners(): void {
    // TODO: 重构为MQTT架构
    console.log('DeviceManager: setupEventListeners - 已禁用，等待MQTT重构');
    /*
    // 监听设备状态变化
    deviceStatusService.on('deviceStatusChange', (deviceInfo: DeviceConnectionInfo) => {
      this.handleDeviceStatusChange(deviceInfo);
    });
    
    // 设备列表更新和统计信息更新现在通过REST API获取，不再通过WebSocket
    console.log('DeviceManager: 设备列表和统计信息更新将通过REST API获取');
    
    // 监听连接状态变化
    deviceStatusService.on('connectionStateChange', (state: string) => {
      this.emit('connectionStateChanged', state);
    });
    
    // 监听错误
    deviceStatusService.on('error', (error: Error) => {
      this.emit('error', error);
    });
    */
  }
  
  /**
   * 处理设备状态变化
   */
  private handleDeviceStatusChange(connectionInfo: any): void {
    const deviceId = connectionInfo.deviceId;
    
    if (this.devices.has(deviceId)) {
      // 更新设备状态
      const device = this.devices.get(deviceId)!;
      device.updateConnectionStatus(connectionInfo.isOnline);
      this.emit('deviceStatusChanged');
    } else {
      // 添加新设备
      const deviceInfo: DeviceInfo = {
        id: connectionInfo.deviceId,
        ipAddress: connectionInfo.deviceIp,
        tcpPort: connectionInfo.tcpPort,
        udpPort: connectionInfo.udpAudioPort || connectionInfo.tcpPort + 1, // 如果没有UDP端口，使用TCP端口+1
        name: `${connectionInfo.deviceIp}:${connectionInfo.tcpPort}`
      };
      
      this.addDevice(deviceInfo);
    }
  }
  
  /**
   * 处理设备列表更新
   */
  private handleDeviceListUpdate(devicesInfo: any[]): void {
    // 清除不在新列表中的设备
    const newDeviceIds = new Set(devicesInfo.map(d => d.deviceId));
    
    Array.from(this.devices.keys()).forEach(deviceId => {
      if (!newDeviceIds.has(deviceId)) {
        this.removeDevice(deviceId);
      }
    });
    
    // 添加或更新设备
    devicesInfo.forEach(connectionInfo => {
      const deviceId = connectionInfo.deviceId;
      
      if (this.devices.has(deviceId)) {
        // 更新设备状态
        const device = this.devices.get(deviceId)!;
        device.updateConnectionStatus(connectionInfo.isOnline);
      } else {
        // 添加新设备
        const deviceInfo: DeviceInfo = {
          id: connectionInfo.deviceId,
          ipAddress: connectionInfo.deviceIp,
          tcpPort: connectionInfo.tcpPort,
          udpPort: connectionInfo.udpAudioPort || connectionInfo.tcpPort + 1,
          name: `${connectionInfo.deviceIp}:${connectionInfo.tcpPort}`
        };
        
        this.addDevice(deviceInfo);
      }
    });
    
    // 触发设备列表更新事件
    this.emit('deviceListUpdated', this.getAllDevices());
  }
  
  /**
   * 处理统计信息更新
   */
  private handleStatisticsUpdate(statistics: any): void {
    const stats = this.getStatistics();
    this.emit('statisticsUpdated', stats);
  }
  
  public addDevice(deviceInfo: DeviceInfo): Device {
    const device = new Device(deviceInfo);
    this.devices.set(deviceInfo.id, device);
    
    // 监听设备状态变化
    device.on('statusChanged', () => {
      this.emit('deviceStatusChanged');
    });
    
    return device;
  }
  
  public removeDevice(deviceId: string): boolean {
    const device = this.devices.get(deviceId);
    if (device) {
      device.dispose();
      this.devices.delete(deviceId);
      this.emit('deviceStatusChanged');
      return true;
    }
    return false;
  }
  
  public getDevice(deviceId: string): Device | undefined {
    return this.devices.get(deviceId);
  }
  
  public getAllDevices(): Device[] {
    return Array.from(this.devices.values());
  }
  
  public getOnlineDevices(): Device[] {
    return this.getAllDevices().filter(device => device.isOnline());
  }
  
  // 批量操作
  public async connectAll(): Promise<void> {
    const promises = this.getAllDevices().map(device => device.connect());
    await Promise.allSettled(promises);
  }
  
  public async disconnectAll(): Promise<void> {
    const promises = this.getAllDevices().map(device => device.disconnect());
    await Promise.allSettled(promises);
  }
  
  public getStatistics(): DeviceManagerStatistics {
    const allDevices = this.getAllDevices();
    const onlineDevices = this.getOnlineDevices();
    
    return {
      total: allDevices.length,
      online: onlineDevices.length,
      audioEnabled: onlineDevices.filter(d => d.isAudioEnabled()).length,
      pttActive: onlineDevices.filter(d => d.isPttActive()).length
    };
  }
  
  // 从后端API获取设备列表 - 现在使用REST API
  public async fetchDevices(): Promise<void> {
    try {
      // 如果设备管理器未初始化，直接返回，避免循环调用
      if (!this.isInitialized) {
        console.log('DeviceManager: 设备管理器未初始化，跳过设备列表获取');
        return;
      }
      
      // 通过REST API获取设备列表
      console.log('DeviceManager: 通过REST API获取设备列表');
      
      // 调用后端API获取设备列表
      const deviceConnections = await deviceApiService.getAllDevices();
      
      // 清空现有设备列表
      this.devices.clear();
      
      // 将后端返回的设备连接信息转换为前端设备信息
        // 同时从 /api/devices/my-session 获取完整的设备信息（包含name字段）
      try {
        const baseUrl = ApiClient.getBaseUrl();
        const token = ApiClient.getAuthToken();
        const sessionResponse = await fetch(`${baseUrl}/devices/my-session`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        
        let deviceNameMap: Map<number, string> = new Map();
        if (sessionResponse.ok) {
          const sessionResult = await sessionResponse.json();
          if (sessionResult.success && sessionResult.data) {
            sessionResult.data.forEach((device: any) => {
              if (device.id && device.name) {
                deviceNameMap.set(device.id, device.name);
              }
            });
          }
        }
        
        deviceConnections.forEach(connection => {
          // 从nameMap中获取设备名称，如果没有则使用默认格式
          const deviceName = connection.deviceId && deviceNameMap.has(connection.deviceId)
            ? deviceNameMap.get(connection.deviceId)!
            : (connection.deviceId ? `设备 #${connection.deviceId}` : `${connection.deviceIp}:${connection.tcpPort}`);
          
          const deviceInfo: DeviceInfo = {
            id: connection.deviceId?.toString() || connection.deviceIp, // 优先使用设备ID，回退到IP地址
            ipAddress: connection.deviceIp,
            tcpPort: connection.tcpPort,
            udpPort: connection.udpPort,
            name: deviceName
          };
          
          this.addDevice(deviceInfo);
        });
      } catch (error) {
        console.warn('DeviceManager: 获取设备名称失败，使用默认名称:', error);
        // 如果获取名称失败，使用原来的逻辑
        deviceConnections.forEach(connection => {
          const deviceInfo: DeviceInfo = {
            id: connection.deviceId?.toString() || connection.deviceIp,
            ipAddress: connection.deviceIp,
            tcpPort: connection.tcpPort,
            udpPort: connection.udpPort,
            name: connection.deviceId ? `设备 #${connection.deviceId}` : `${connection.deviceIp}:${connection.tcpPort}`
          };
          
          this.addDevice(deviceInfo);
        });
      }
      
      // 触发设备列表更新事件
      this.emit('deviceListUpdated', this.getAllDevices());
      
      console.log('DeviceManager: 设备列表获取完成，当前设备数量:', this.devices.size);
      
    } catch (error) {
      console.error('DeviceManager: 获取设备列表失败:', error);
      // 如果API调用失败，使用空列表而不是模拟数据
      this.devices.clear();
      this.emit('deviceListUpdated', this.getAllDevices());
      this.emit('error', error as Error);
      throw error;
    }
  }
  
  // 事件系统
  public on<K extends keyof DeviceManagerEvents>(event: K, callback: DeviceManagerEvents[K]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(callback);
    }
  }
  
  public off<K extends keyof DeviceManagerEvents>(event: K, callback: DeviceManagerEvents[K]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }
  
  private emit<K extends keyof DeviceManagerEvents>(event: K, ...args: any[]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(...args));
    }
  }
  
  /**
   * 检查WebSocket连接状态
   */
  public isWebSocketConnected(): boolean {
    // TODO: 重构为MQTT架构
    // return deviceStatusService.isWebSocketConnected();
    return false;
  }
  
  /**
   * 获取WebSocket连接状态
   */
  public getConnectionState(): string {
    // TODO: 重构为MQTT架构
    // return deviceStatusService.getConnectionState();
    return 'disconnected';
  }
  
  /**
   * 检查设备管理器是否已初始化
   */
  public getInitializationStatus(): boolean {
    return this.isInitialized;
  }

  /**
   * 基础初始化（不包含fetchDevices调用，避免循环）
   */
  public async dispose(): Promise<void> {
    try {
      // 断开所有设备连接
      await this.disconnectAll();
      
      // 清空设备列表
      this.devices.clear();
      
      // 销毁设备状态服务
      // TODO: 重构为MQTT架构
      // await deviceStatusService.dispose();
      
      // 清理事件监听
      this.eventListeners.forEach(listeners => listeners.clear());
      
      this.isInitialized = false;
      
    } catch (error) {
      console.error('DeviceManager: 销毁失败:', error);
      throw error;
    }
  }
}